/**
 * File and path utilities
 */

import { fileName } from 'types/staticValues';

/**
 * Maximum file size for TUS uploads - 128 GB (decimal) in bytes
 */

export const MAX_TUS_FILE_SIZE_GB = 128;
export const MAX_TUS_FILE_SIZE = MAX_TUS_FILE_SIZE_GB * 1000 * 1000 * 1000;

/**
 * Maximum total size for files being zipped in the browser
 * Set to 5 GB to avoid browser memory issues with JSZip
 */
export const MAX_BROWSER_ZIP_SIZE_GB = 4;
export const MAX_BROWSER_ZIP_SIZE = MAX_BROWSER_ZIP_SIZE_GB * 1000 * 1000 * 1000;

/**
 * Creates a jobscript file from content
 */
export const createJobscriptFile = (content: string): File => {
    const blob = new Blob([content], { type: 'text/plain' });
    return new File([blob], fileName, { type: 'text/plain' });
};

/**
 * Extracts dataset ID from dataset path
 */
export const getDatasetIdFromDatasetPath = (path: string): string | undefined => {
    const splittedJobScriptPath = path.split('/');
    return splittedJobScriptPath?.[splittedJobScriptPath.length - 1];
};

/**
 * Deletes root directory ID/name from path
 */
export const deleteRootDirectoryIdName = (
    path: string | undefined,
    rootName: string | undefined
): string | undefined => {
    if (!path) return path;

    let cleanedPath = path;

    if (rootName && cleanedPath.startsWith(`${rootName}/`)) {
        cleanedPath = cleanedPath.replace(`${rootName}/`, '');
    }

    if (cleanedPath.startsWith('/')) {
        cleanedPath = cleanedPath.substring(1);
    }

    return cleanedPath;
};

/**
 * Validates if a file size is within the TUS upload limit
 * @param file - File to validate
 * @returns true if valid, error message string if invalid
 */
export const validateTusFileSize = (file: File | null): true | string => {
    if (!file) {
        return true;
    }

    if (file.size > MAX_TUS_FILE_SIZE) {
        return `File size exceeds the maximum limit of ${MAX_TUS_FILE_SIZE_GB} GB. For larger files, please use Py4Lexis.`;
    }

    return true;
};

/**
 * Formats file size for display in error messages
 * @param bytes - File size in bytes
 * @returns Formatted file size string in GB
 */

export const formatFileSizeGB = (bytes: number): string => {
    return (bytes / (1000 * 1000 * 1000)).toFixed(2);
};

/**
 * Checks if a file has an archive extension
 */
export const isArchiveFile = (fileName: string): boolean => {
    if (!fileName) return false;
    const archiveExtensions = ['.zip', '.tar.gz', '.tgz', '.tar', '.rar', '.7z', '.bz2'];
    return archiveExtensions.some((ext) => fileName.toLowerCase().endsWith(ext));
};
